// ComplexOrderHistoryItem.tsx
import React from "react";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import { MdRepeat, MdPhone } from "react-icons/md";
import styles from "./ComplexOrderHistoryItem.module.scss";
import { ComplexOrderInterface } from "../../../../../common/types/types";
import { useTheme } from "../../../../../common/hooks/useTheme";

interface ComplexOrderHistoryItemProps {
  order: ComplexOrderInterface;
  onPress: (orderId: number) => void;
  onRepeat?: (order: ComplexOrderInterface) => void;
  getOrderStatusInfo: (status: number) => {
    text: string;
    backgroundColor: string;
    textColor: string;
  };
}

const ComplexOrderHistoryItem: React.FC<ComplexOrderHistoryItemProps> = ({
  order,
  onPress,
  onRepeat,
  getOrderStatusInfo,
}) => {
  const { theme } = useTheme();

  const handlePress = () => onPress(Number(order.id));
  const handleRepeat = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRepeat?.(order);
  };

  const statusInfo = getOrderStatusInfo(order.status);

  return (
    <div
      className={styles.orderCard}
      onClick={handlePress}
      style={{
        backgroundColor: theme.card,
        borderColor: theme.border,
      }}
    >
      <div className={styles.orderHeader}>
        <span className={styles.orderId} style={{ color: theme.text }}>
          Заявка №{order.id}
        </span>
        {order.createdAt && (
          <span
            className={styles.orderDate}
            style={{ color: theme.textSecondary }}
          >
            {format(new Date(order.createdAt), "dd.MM.yyyy HH:mm", {
              locale: ru,
            })}
          </span>
        )}
      </div>

      <div
        className={styles.orderStatus}
        style={{ backgroundColor: `${statusInfo.backgroundColor}` }}
      >
        <span
          className={styles.statusText}
          style={{ color: statusInfo.textColor }}
        >
          {statusInfo.text}
        </span>
      </div>

      <div className={styles.orderInfo}>
        <div className={styles.infoRow}>
          <MdPhone size={16} color={theme.primary} />
          <span className={styles.infoText} style={{ color: theme.text }}>
            {order.phone_number || "Телефон не указан"}
          </span>
        </div>
      </div>

      {order.items && order.items.length > 0 && (
        <div className={styles.orderItems}>
          <span
            className={styles.itemsTitle}
            style={{ color: theme.textSecondary }}
          >
            Состав заявки:
          </span>
          {order.items.slice(0, 2).map((orderItem, index) => (
            <div key={index} className={styles.itemRow}>
              <span className={styles.itemName} style={{ color: theme.text }}>
                {orderItem.name}
              </span>
              <div className={styles.itemDetails}>
                <span
                  className={styles.itemQuantity}
                  style={{ color: theme.textSecondary }}
                >
                  {orderItem.quantity} ×
                </span>
                <span
                  className={styles.itemPrice}
                  style={{ color: theme.text }}
                >
                  {orderItem.total > 0
                    ? `${Number(orderItem.total).toLocaleString("ru-RU")} ₽`
                    : "—"}
                </span>
              </div>
            </div>
          ))}
          {order.items.length > 2 && (
            <span className={styles.moreItems} style={{ color: theme.primary }}>
              +{order.items.length - 2} еще...
            </span>
          )}
        </div>
      )}

      <div
        className={styles.orderFooter}
        style={{ borderTopColor: theme.border }}
      >
        <div className={styles.totalInfo}>
          <span
            className={styles.totalLabel}
            style={{ color: theme.textSecondary }}
          >
            Итого:
          </span>
          <span className={styles.totalAmount} style={{ color: theme.primary }}>
            {order.total > 0
              ? `${Number(order.total).toLocaleString("ru-RU")} ₽`
              : "—"}
          </span>
        </div>

        {onRepeat && (
          <button className={styles.repeatButton} onClick={handleRepeat}>
            <MdRepeat size={18} color="#fff" />
            <span className={styles.repeatText}>Повторить</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ComplexOrderHistoryItem;
